import { supabase } from "@/integrations/supabase/client";
import { Tables } from "@/integrations/supabase/types";

export type EmailTemplateConfig = Tables<'email_template_configs'>;

export interface TemplateAnalytics {
  template_key: string;
  template_name: string;
  total_sent: number;
  total_delivered: number;
  total_opened: number;
  total_clicked: number;
  delivery_rate: number;
  open_rate: number;
  click_rate: number;
}

export interface UpdateTemplateConfigData {
  template_name?: string;
  description?: string | null;
  enabled?: boolean;
  from_name?: string;
  from_email?: string;
  reply_to?: string | null;
  cc_emails?: string[] | null;
  bcc_emails?: string[] | null;
  sample_data?: Record<string, any>;
}

/**
 * Get all email template configurations
 */
export const getAllTemplates = async (): Promise<EmailTemplateConfig[]> => {
  const { data, error } = await supabase
    .from('email_template_configs')
    .select('*')
    .order('category', { ascending: true })
    .order('template_name', { ascending: true });

  if (error) {
    console.error('Error fetching email templates:', error);
    throw new Error(`Failed to fetch email templates: ${error.message}`);
  }

  return (data || []) as EmailTemplateConfig[];
};

/**
 * Get a specific template configuration by key
 */
export const getTemplateByKey = async (
  templateKey: string
): Promise<EmailTemplateConfig | null> => {
  const { data, error } = await supabase
    .from('email_template_configs')
    .select('*')
    .eq('template_key', templateKey)
    .maybeSingle();

  if (error) {
    console.error('Error fetching template:', error);
    throw new Error(`Failed to fetch template: ${error.message}`);
  }

  return data as EmailTemplateConfig | null;
};

/**
 * Update template configuration
 */
export const updateTemplateConfig = async (
  templateKey: string,
  updates: UpdateTemplateConfigData
): Promise<EmailTemplateConfig> => {
  const { data, error } = await supabase
    .from('email_template_configs')
    .update(updates)
    .eq('template_key', templateKey)
    .select()
    .single();

  if (error) {
    console.error('Error updating template config:', error);
    throw new Error(`Failed to update template: ${error.message}`);
  }

  return data as EmailTemplateConfig;
};

/**
 * Toggle template enabled status
 */
export const toggleTemplateEnabled = async (
  templateKey: string,
  enabled: boolean
): Promise<EmailTemplateConfig> => {
  return updateTemplateConfig(templateKey, { enabled });
};

/**
 * Get template analytics with calculated rates
 */
export const getTemplateAnalytics = async (
  templateKey?: string
): Promise<TemplateAnalytics[]> => {
  let query = supabase.from('email_template_configs').select('*');

  if (templateKey) {
    query = query.eq('template_key', templateKey);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching template analytics:', error);
    throw new Error(`Failed to fetch analytics: ${error.message}`);
  }

  return (data || []).map((template) => ({
    template_key: template.template_key,
    template_name: template.template_name,
    total_sent: template.total_sent,
    total_delivered: template.total_delivered,
    total_opened: template.total_opened,
    total_clicked: template.total_clicked,
    delivery_rate: template.total_sent > 0 
      ? (template.total_delivered / template.total_sent) * 100 
      : 0,
    open_rate: template.total_delivered > 0 
      ? (template.total_opened / template.total_delivered) * 100 
      : 0,
    click_rate: template.total_opened > 0 
      ? (template.total_clicked / template.total_opened) * 100 
      : 0,
  }));
};

/**
 * Generate HTML preview of a template using the edge function
 */
export const generatePreviewHtml = async (
  templateKey: string,
  previewData?: Record<string, any>
): Promise<string> => {
  const template = await getTemplateByKey(templateKey);
  
  if (!template) {
    throw new Error(`Template not found: ${templateKey}`);
  }

  // Use provided preview data or template's sample data
  const emailData = previewData || template.sample_data;

  const { data, error } = await supabase.functions.invoke('send-notification-email', {
    body: {
      templateKey,
      emailData,
      previewOnly: true, // Special flag to only render HTML without sending
    },
  });

  if (error) {
    console.error('Error generating preview:', error);
    throw new Error(`Failed to generate preview: ${error.message}`);
  }

  return data.html;
};

/**
 * Send a test email using real or sample data
 */
export const sendTestEmail = async (
  templateKey: string,
  recipientEmail: string,
  testData?: Record<string, any>
): Promise<{ success: boolean; message: string }> => {
  const template = await getTemplateByKey(templateKey);
  
  if (!template) {
    throw new Error(`Template not found: ${templateKey}`);
  }

  if (!template.enabled) {
    throw new Error(`Template is disabled: ${templateKey}`);
  }

  // Use provided test data or template's sample data
  const emailData = testData || template.sample_data;

  const { data, error } = await supabase.functions.invoke('send-notification-email', {
    body: {
      templateKey,
      recipientEmail,
      emailData,
      isTest: true, // Flag to indicate this is a test email
    },
  });

  if (error) {
    console.error('Error sending test email:', error);
    throw new Error(`Failed to send test email: ${error.message}`);
  }

  return {
    success: true,
    message: `Test email sent successfully to ${recipientEmail}`,
  };
};

/**
 * Get templates by category
 */
export const getTemplatesByCategory = async (
  category: string
): Promise<EmailTemplateConfig[]> => {
  const { data, error } = await supabase
    .from('email_template_configs')
    .select('*')
    .eq('category', category)
    .order('template_name', { ascending: true });

  if (error) {
    console.error('Error fetching templates by category:', error);
    throw new Error(`Failed to fetch templates: ${error.message}`);
  }

  return (data || []) as EmailTemplateConfig[];
};

/**
 * Validate template data against required variables
 */
export const validateTemplateData = (
  template: EmailTemplateConfig,
  data: Record<string, any>
): { valid: boolean; missingVariables: string[] } => {
  const requiredVars = (template.required_variables as string[]) || [];
  const missingVariables = requiredVars.filter(
    (variable) => !(variable in data) || data[variable] === undefined || data[variable] === null
  );

  return {
    valid: missingVariables.length === 0,
    missingVariables,
  };
};
